import { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { DocumentItem } from '@vedjegy/shared';

type DocWithKey = DocumentItem & { fileKey?: string };

export default function AdminDocumentsPage() {
  const [docs, setDocs]           = useState<DocWithKey[]>([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const [renameId, setRenameId]   = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.documents.list()
      .then((r) => setDocs(r.documents))
      .catch(() => setError('Betöltés sikertelen.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleUpload(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true); setError('');
    try {
      for (const file of Array.from(files)) {
        const { docId, uploadUrl, fileKey } = await api.documents.getUploadUrl({
          fileName: file.name, mimeType: file.type,
        });
        // Dev mode returns /api/admin/documents/dev-file; pass file key via header.
        // Production S3 presigned URLs are external and must not receive auth headers.
        const isDevUpload = uploadUrl.startsWith('/api/');
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            ...(isDevUpload ? { 'X-File-Key': fileKey } : {}),
          },
          credentials: isDevUpload ? 'include' : 'omit',
        });
        const doc = await api.documents.register({
          docId, fileKey, displayName: file.name, fileSize: file.size, mimeType: file.type,
        });
        setDocs((d) => [doc, ...d]);
      }
    } catch {
      setError('Feltöltés sikertelen.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleRename(id: string) {
    if (!renameVal.trim()) return;
    await api.documents.rename(id, { displayName: renameVal.trim() });
    setDocs((d) => d.map((doc) => doc.id === id ? { ...doc, displayName: renameVal.trim() } : doc));
    setRenameId(null);
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Törli ezt a dokumentumot: „${name}"?`)) return;
    await api.documents.remove(id);
    setDocs((d) => d.filter((doc) => doc.id !== id));
  }

  const fmtSize = (b: number | null) => b == null ? '—' : b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>Dokumentumtár</h1>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-foreground)' }}>
              Útmutatók és minta pályázatok — pályázók számára is látható.
            </p>
          </div>
          <label style={{ cursor: uploading ? 'wait' : 'pointer' }}>
            <input
              ref={fileRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => void handleUpload(e.target.files)}
            />
            <span className="btn btn-primary" style={{ pointerEvents: uploading ? 'none' : 'auto', opacity: uploading ? 0.6 : 1 }}>
              {uploading ? 'Feltöltés...' : '+ Dokumentum feltöltése'}
            </span>
          </label>
        </div>

        {error && <div style={{ fontSize: 13, color: '#c0392b', marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <p style={{ color: 'var(--muted-foreground)' }}>Betöltés...</p>
        ) : docs.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>
            Még nincs feltöltött dokumentum.
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Név</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Méret</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Feltöltve</th>
                  <th style={{ padding: '10px 16px' }} />
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => (
                  <tr key={doc.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 16px' }}>
                      {renameId === doc.id ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            autoFocus
                            value={renameVal}
                            onChange={(e) => setRenameVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') void handleRename(doc.id); if (e.key === 'Escape') setRenameId(null); }}
                            style={{ padding: '4px 8px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', width: 280 }}
                          />
                          <button className="btn btn-primary btn-sm" style={{ fontSize: 12 }} onClick={() => void handleRename(doc.id)}>Mentés</button>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setRenameId(null)}>Mégsem</button>
                        </div>
                      ) : (
                        <span style={{ fontWeight: 500, color: 'var(--primary)' }}>{doc.displayName}</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--muted-foreground)' }}>{fmtSize(doc.fileSize)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--muted-foreground)' }}>{new Date(doc.createdAt).toLocaleDateString('hu-HU')}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {doc.downloadUrl && (
                        <a href={doc.downloadUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12, marginRight: 4 }}>
                          ↓ Letöltés
                        </a>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 12, marginRight: 4 }}
                        onClick={() => { setRenameId(doc.id); setRenameVal(doc.displayName); }}
                      >
                        Átnevezés
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ fontSize: 12, color: '#c0392b' }}
                        onClick={() => void handleDelete(doc.id, doc.displayName)}
                      >
                        Törlés
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
